const dialogOpeners = new WeakMap();

const resolveDialog = (trigger) => {
  const selector = trigger.getAttribute('data-dialog-open');
  return selector ? document.querySelector(selector) : null;
};

const resolveProductDetailsDialog = (trigger) => {
  const sectionId = trigger.getAttribute('data-product-details-open');
  if (!sectionId) return null;

  return Array.from(document.querySelectorAll('[id^="StellrProductDetails-"]')).find((dialog) =>
    dialog.id === `StellrProductDetails-${sectionId}` || dialog.id.endsWith(`__${sectionId}`),
  );
};

const openDialog = (dialog, opener) => {
  if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
  dialogOpeners.set(dialog, opener);
  dialog.showModal();
  const focusTarget = dialog.querySelector('[autofocus], input, button, a[href]');
  focusTarget?.focus();
};

const closeDialog = (dialog) => {
  if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
  dialog.close();
  dialogOpeners.get(dialog)?.focus();
  dialogOpeners.delete(dialog);
};

const bindDialogEvents = () => {
  if (document.documentElement.dataset.stellrDialogsBound === 'true') return;
  document.documentElement.dataset.stellrDialogsBound = 'true';

  document.addEventListener('click', (event) => {
    const productDetailsTrigger = event.target.closest('[data-product-details-open]');
    if (productDetailsTrigger) {
      const dialog = resolveProductDetailsDialog(productDetailsTrigger);
      if (dialog) {
        event.preventDefault();
        openDialog(dialog, productDetailsTrigger);
      }
      return;
    }

    const openTrigger = event.target.closest('[data-dialog-open]');
    if (openTrigger) {
      const dialog = resolveDialog(openTrigger);
      if (dialog) {
        event.preventDefault();
        openDialog(dialog, openTrigger);
      }
      return;
    }

    const closeTrigger = event.target.closest('[data-dialog-close]');
    if (closeTrigger) {
      event.preventDefault();
      closeDialog(closeTrigger.closest('dialog'));
      return;
    }

    if (event.target instanceof HTMLDialogElement) {
      const bounds = event.target.getBoundingClientRect();
      const inside = event.clientX >= bounds.left && event.clientX <= bounds.right && event.clientY >= bounds.top && event.clientY <= bounds.bottom;
      if (!inside) closeDialog(event.target);
    }
  });

  document.addEventListener('cancel', (event) => {
    if (event.target instanceof HTMLDialogElement) {
      event.preventDefault();
      closeDialog(event.target);
    }
  });
};

const bindHoverVideos = (root = document) => {
  root.querySelectorAll('[data-stellr-video-hover]').forEach((container) => {
    if (container.dataset.stellrVideoHoverBound === 'true') return;
    const video = container.querySelector('video');
    if (!video) return;

    container.dataset.stellrVideoHoverBound = 'true';
    video.pause();

    const play = () => {
      video.play().catch(() => {});
    };
    const pause = () => {
      video.pause();
    };

    container.addEventListener('pointerenter', play);
    container.addEventListener('pointerleave', pause);
    container.addEventListener('focusin', play);
    container.addEventListener('focusout', pause);
  });
};

const smoothScrollTo = (target) => {
  if (!target) return;

  const smoother = window.ScrollSmoother?.get?.();
  if (smoother) {
    smoother.scrollTo(target, true, 'top top');
    return;
  }

  const targetTop = target.getBoundingClientRect().top + window.scrollY;
  window.scrollTo(0, targetTop);
};

const bindSmoothAnchors = () => {
  if (document.documentElement.dataset.stellrSmoothAnchorsBound === 'true') return;
  document.documentElement.dataset.stellrSmoothAnchorsBound = 'true';

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-stellr-smooth-anchor]');
    if (!link) return;

    const hash = link.getAttribute('href');
    if (!hash || !hash.startsWith('#') || hash.length < 2) return;

    const target = document.querySelector(hash);
    if (!target) return;

    event.preventDefault();
    smoothScrollTo(target);

    if (window.history?.pushState) {
      window.history.pushState(null, '', hash);
    }
  });
};

const refresh = () => {
  document.documentElement.classList.remove('no-js');
  bindDialogEvents();
  bindHoverVideos();
  bindSmoothAnchors();
};

window.StellrTheme = { openDialog, closeDialog, refresh, smoothScrollTo };

document.addEventListener('DOMContentLoaded', refresh);
document.addEventListener('shopify:section:load', refresh);
